import { toast } from "react-toastify";
import * as ApiLocations from "api-calls/ApiLocations";
import { apiPost, apiPostFilesAxios } from "api-calls/ApiCommands";
import { getCreatedDateTimeOfFNOL } from "shared/FnolActions";
import ErrorToastContent from "components/modals/ErrorToastContent";
import { getRecoil, setRecoil } from "recoil-nexus";
import { jobIdState } from "atoms/jobIdState.atom";
import { fnolPageDeciderState } from "atoms/fnolPageDeciderState.atom";
import { TAKINGFNOLSUBMIT_PAGE } from "constants/PageNameConstants";
import { documentUploadFilesState } from "atoms/documentUploadFilesState.atom";
import { isNullOrLengthIsZero } from "shared/Helpers";
import ErrorListToast from "components/modals/ErrorListToast";
import store from "store/appStore";
import { setSubmissionStarted, setPartialCompletion, selectSubmissionStarted } from "slices/fnolSubmissionSlice";

async function uploadFilesToFnol(tempDocumentsId, documents) {
  if (isNullOrLengthIsZero(documents.length)) {
    return true;
  }

  try {
    const url = await ApiLocations.getUploadFilesToFnolUrl(tempDocumentsId);
    const response = await apiPostFilesAxios(url, documents);
    return response.status === 200 ? true : false;
  } catch (error) {
    console.error("Upload failed:", error);
    return false;
  }
}

export const createFnolInstance = async (fnolInstance) => {
  const documents = getRecoil(documentUploadFilesState);

  // Upload documents first (existing behavior - unchanged)
  const fileUploadSuccess = await uploadFilesToFnol(fnolInstance.tempDocumentsId, documents);

  if (fileUploadSuccess === false) {
    setRecoil(documentUploadFilesState, []);
    toast.error(
      <ErrorListToast
        errorTitle={"Unable to save FNOL"}
        errorBody={"Document upload failed. Please re-select documents and try again."}
        errorList={documents.map((file) => file.name)}
      />,
      {
        position: "bottom-center",
        autoClose: false,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: false,
        draggable: false,
      }
    );
    return; // Allow retry - file upload failed before FNOL creation
  }

  let fnolId = null;

  try {
    // STAGE 1: Create FNOL
    const createUrl = await ApiLocations.getCreateFnolInstanceURL();
    const dateTimeWhenFNOLOpened = getCreatedDateTimeOfFNOL();

    const newFnolInstance = {
      fnol: fnolInstance,
      createdDateTimeOfFNOL: dateTimeWhenFNOLOpened,
    };

    var fnolInstanceBody = JSON.stringify(newFnolInstance);

    const createFnolResult = await apiPost(createUrl, fnolInstanceBody);

    if (!createFnolResult || !createFnolResult.fnolId) {
      throw new Error("Stage 1 failed: No fnolId returned");
    }

    fnolId = createFnolResult.fnolId;
    const { tempDocumentsId: responseTempDocumentsId } = createFnolResult;

    // FNOL created successfully - signal to disable submit button permanently
    // (This is the critical point - even if stage 2 fails, we must not allow resubmit)
    store.dispatch(setSubmissionStarted(true));

    // Navigate to success page immediately with "pending" status
    store.dispatch(setPartialCompletion({
      status: "pending",
      jobId: null,
      fnolId: fnolId
    }));
    setRecoil(fnolPageDeciderState, { stage: TAKINGFNOLSUBMIT_PAGE });

    // STAGE 2: Complete FNOL (get jobId)
    const completeUrl = await ApiLocations.getCompleteFnolUrl(fnolId);
    const completeBody = JSON.stringify({ tempDocumentsId: responseTempDocumentsId });

    const completeJobSetupResult = await apiPost(completeUrl, completeBody);

    if (!completeJobSetupResult) {
      throw new Error("Stage 2 failed: No response");
    }

    const jobId = completeJobSetupResult.toString();
    setRecoil(jobIdState, jobId);
    store.dispatch(setPartialCompletion({
      status: "complete",
      jobId: jobId,
      fnolId: fnolId
    }));

  } catch (err) {
    // Check if stage 1 already succeeded (submissionStarted is true)
    const fnolAlreadyCreated = selectSubmissionStarted(store.getState());

    if (fnolAlreadyCreated) {
      // Stage 2 failed but FNOL exists - already on success page, just update status
      // Try to extract jobId from error response (API may return it even on failure)
      const jobIdFromError = err.jobId || err.JobId || null;

      store.dispatch(setPartialCompletion({
        status: "complete",
        jobId: jobIdFromError,
        fnolId: fnolId
      }));
    } else {
      // Stage 1 failed - show error (user can retry)
      toast(
        <ErrorToastContent
          errorId={err.status}
          title="Couldn't save your FNOL. Please try again or contact IT Support."
          exception={err.title || err.message}
        />,
        {
          position: "top-center",
          className: "customToastError",
          autoClose: false,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: false,
          draggable: false,
        }
      );
    }
  }
};
